import {
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Text,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useEffect, useRef, useState, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';

import { registerSuccess } from '@/store/actions/register';
import { useGetPublicPartnerData } from '@/v5/@hooks/auth/login/useGetPublicPartnerData';
import { useRegister } from '@/v5/@hooks/auth/useRegister';
import { useRegisterForm } from '@/v5/@hooks/auth/useRegisterForm';
import { registerValidationSchema } from '@/v5/validation/authValidation';

import { SubmitButton } from '@/v5/components/atoms/Button/SubmitButton';
import { TcapHeading } from '@/v5/components/atoms/Heading/TcapHeading';
import { ConfirmPasswordField } from '@/v5/components/molecules/FormField/ConfirmPasswordField';
import { EmailField } from '@/v5/components/molecules/FormField/EmailField';
import { PasswordValidationField } from '@/v5/components/molecules/FormField/PasswordValidationField';
import { RegistrationCodeFields } from '@/v5/components/molecules/FormField/RegistrationCodeFields';
import { TextField } from '@/v5/components/molecules/FormField/TextField';
import { gridLabelStyles } from '@/v5/@constants/register';

function RegisterForm() {
    const dispatch = useDispatch();
    const { handleSubmit, isLoading, resetMessages, success } = useRegister();
    const { initialValues } = useRegisterForm();
    const [tosAccepted, setTosAccepted] = useState(false);
    const [tosError, setTosError] = useState(null);
    const tosErrorMsg = 'You must accept the Terms of Service.';
    const recaptchaRef = useRef();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const [recaptchaExpired, setRecaptchaExpired] = useState(false);

    const registerSuccessState = useSelector(
        (state) => state.login.registerSuccess,
    );

    const { data: publicPartnerData } = useGetPublicPartnerData();

    const handleReCaptchaChange = useCallback(
        (token) => {
            if (!token || !pendingSubmission) return;

            setRecaptchaExpired(false);
            const { values, formikHelpers } = pendingSubmission;
            const updatedValues = { ...values, recaptchaToken: token };

            onClose();
            handleSubmit(updatedValues, formikHelpers);
            setPendingSubmission(null);
        },
        [pendingSubmission, onClose, handleSubmit],
    );

    const handleReCaptchaExpired = useCallback(() => {
        setRecaptchaExpired(true);
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    }, []);

    const handleModalClose = useCallback(() => {
        if (pendingSubmission) {
            pendingSubmission.formikHelpers.setSubmitting(false);
            setPendingSubmission(null);
        }
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
        setRecaptchaExpired(false);
        onClose();
    }, [pendingSubmission, onClose]);

    // When user clicks "Create Account" - validate and open modal
    const handleFormSubmit = (values, formikHelpers) => {
        const { setSubmitting } = formikHelpers;

        // Validate TOS if required
        if (
            publicPartnerData?.extLinkRegistrationTerms ||
            publicPartnerData?.extLinkPrivacyPolicy
        ) {
            if (!tosAccepted) {
                setTosError(tosErrorMsg);
                setSubmitting(false);
                return;
            } else {
                setTosError(null);
            }
        }

        setPendingSubmission({ values, formikHelpers });
        setRecaptchaExpired(false);
        onOpen();
    };

    useEffect(() => {
        if (success) {
            dispatch(registerSuccess(initialValues.email));
        }
    }, [success, dispatch, initialValues.email]);

    const showSuccess = registerSuccessState || success;

    if (showSuccess) {
        return (
            <VStack spacing={6} align="stretch" px="1px" textAlign="center">
                <TcapHeading>Registration Successful!</TcapHeading>
                <Text color="gray.600">
                    Please check your email for activation instructions.
                </Text>
            </VStack>
        );
    }

    return (
        <VStack spacing={6} align="stretch" px="1px">
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={registerValidationSchema}
                onSubmit={handleFormSubmit}>
                {({ isSubmitting, values, errors }) => (
                    <Form>
                        <VStack spacing={4}>
                            {/* Company Name */}
                            <Field
                                name="companyName"
                                onFocus={resetMessages}
                                component={TextField}
                                label="Company Name"
                                placeholder="Enter your company name"
                                isRequired
                            />

                            {/* First Name, Last Name Row */}
                            <SimpleGrid
                                columns={2}
                                spacing={4}
                                width="full"
                                sx={gridLabelStyles}>
                                <Field
                                    name="firstName"
                                    onFocus={resetMessages}
                                    component={TextField}
                                    label="First Name"
                                    placeholder="Enter your first name"
                                    isRequired
                                />
                                <Field
                                    name="lastName"
                                    onFocus={resetMessages}
                                    component={TextField}
                                    label="Last Name"
                                    placeholder="Enter your last name"
                                    isRequired
                                />
                            </SimpleGrid>

                            {/* Email, Phone Row */}
                            <SimpleGrid
                                columns={2}
                                spacing={4}
                                width="full"
                                sx={gridLabelStyles}>
                                {/* Email */}
                                <Field
                                    name="email"
                                    onFocus={resetMessages}
                                    component={EmailField}
                                    isRequired
                                />
                                {/* Phone Number */}
                                <Field
                                    name="contactNumber"
                                    onFocus={resetMessages}
                                    component={TextField}
                                    label="Phone Number"
                                    placeholder="Enter your phone number"
                                    type="tel"
                                />
                            </SimpleGrid>

                            {/* Password, Confirm Password Row */}
                            <SimpleGrid
                                columns={2}
                                spacing={4}
                                width="full"
                                sx={gridLabelStyles}>
                                <Field
                                    name="password"
                                    onFocus={resetMessages}
                                    component={PasswordValidationField}
                                    label="Password"
                                    isRequired
                                    isOpen={Boolean(
                                        values?.password && errors?.password,
                                    )}
                                />
                                <Field
                                    name="confirmPassword"
                                    onFocus={resetMessages}
                                    component={ConfirmPasswordField}
                                    isRequired
                                />
                            </SimpleGrid>

                            {/* Partner Code & Approval Code (conditional layout) */}
                            <RegistrationCodeFields
                                values={values}
                                resetMessages={resetMessages}
                            />

                            {/* Terms of Service */}
                            {(publicPartnerData?.extLinkRegistrationTerms ||
                                publicPartnerData?.extLinkPrivacyPolicy) && (
                                <FormControl isRequired isInvalid={!!tosError}>
                                    <HStack
                                        spacing={3}
                                        align="flex-start"
                                        sx={{
                                            '& > label': {
                                                mb: 0,
                                                mt: '0.25rem',
                                            },
                                        }}>
                                        <Checkbox
                                            isChecked={tosAccepted}
                                            onChange={(e) => {
                                                setTosAccepted(
                                                    e.target.checked,
                                                );
                                                if (e.target.checked)
                                                    setTosError(null);
                                            }}
                                            colorScheme="brand"
                                            size="md"
                                        />
                                        <FormLabel
                                            fontSize="sm"
                                            color="gray.600"
                                            mb={0}>
                                            By checking this box you have read
                                            and agree to the{' '}
                                            {publicPartnerData?.extLinkRegistrationTerms && (
                                                <Text
                                                    as="a"
                                                    href={
                                                        publicPartnerData.extLinkRegistrationTerms
                                                    }
                                                    target="_blank"
                                                    color="brand.500"
                                                    textDecoration="underline"
                                                    _hover={{
                                                        color: 'brand.600',
                                                    }}>
                                                    Terms and Conditions
                                                </Text>
                                            )}
                                            {publicPartnerData?.extLinkRegistrationTerms &&
                                                publicPartnerData?.extLinkPrivacyPolicy &&
                                                ' and '}
                                            {publicPartnerData?.extLinkPrivacyPolicy && (
                                                <Text
                                                    as="a"
                                                    href={
                                                        publicPartnerData.extLinkPrivacyPolicy
                                                    }
                                                    target="_blank"
                                                    color="brand.500"
                                                    textDecoration="underline"
                                                    _hover={{
                                                        color: 'brand.600',
                                                    }}>
                                                    Privacy Policy
                                                </Text>
                                            )}
                                            {
                                                ' and you are authorized to accept these on behalf of your company.'
                                            }
                                        </FormLabel>
                                    </HStack>
                                    <FormErrorMessage fontSize="sm" mt={1}>
                                        {tosError}
                                    </FormErrorMessage>
                                </FormControl>
                            )}

                            <SubmitButton
                                isSubmitting={isLoading || isSubmitting}
                                loadingText="Creating account..."
                                width="full"
                                className="max-h-10">
                                Create Account
                            </SubmitButton>
                        </VStack>
                    </Form>
                )}
            </Formik>

            {/* ReCAPTCHA Modal */}
            <Modal
                isOpen={isOpen}
                onClose={handleModalClose}
                closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent top="50%">
                    <ModalHeader>Verify you are human</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack align="center">
                            {recaptchaExpired && (
                                <Text color="red.500" fontSize="sm">
                                    ReCAPTCHA expired. Please try again.
                                </Text>
                            )}
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey="6Lci89EUAAAAAHIeIMWNhPxlbJE55ExpBlRkWr93"
                                onChange={handleReCaptchaChange}
                                onExpired={handleReCaptchaExpired}
                            />
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </VStack>
    );
}

export { RegisterForm };
